import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import {
  Code2, ShieldCheck, TrendingUp, MessagesSquare, CheckCircle, ArrowRight,
  Lock, Coins, Puzzle, Layers, Server, Loader2, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRICING_OPTIONS = [
  {
    id: 'private',
    icon: Lock,
    accent: 'from-blue-600 to-cyan-500',
    badge: 'Exclusive to you',
    title: 'Private License Build',
    price: 'Custom Quote',
    tagline: 'You own it — exclusively.',
    description:
      'We scope, design and build your extension to your exact specification. It is licensed privately to you and is never sold to anyone else.',
    features: [
      'Full custom quote for the complete build',
      'Exclusive private license — never resold',
      'Complete source code + documentation delivered',
      'Built to Joomla 4, 5 & 6 standards',
    ],
  },
  {
    id: 'marketed',
    icon: Coins,
    accent: 'from-purple-600 to-indigo-500',
    badge: 'Best value',
    title: 'Co-Marketed Build',
    price: 'Discounted + Royalties',
    tagline: 'Build it for less, earn for life.',
    description:
      "If it's something we want to market and sell in our catalog, we build it at a discounted price — then pay you royalties for the entire life of the product.",
    features: [
      'Reduced build cost when we co-market it',
      'Royalties paid for the life of the product',
      'We handle listing, updates & support',
      'Reach thousands of Joomla site owners',
    ],
    highlighted: true,
  },
  {
    id: 'unsure',
    icon: MessagesSquare,
    accent: 'from-emerald-600 to-teal-500',
    badge: 'No pressure',
    title: "Not Sure Yet? Let's Talk",
    price: 'Free Consultation',
    tagline: "Tell us the idea — we'll advise.",
    description:
      "Not certain which route fits? Share your idea and we'll recommend the best path, whether that's a private build, a co-marketed release, or an existing extension.",
    features: [
      'Free scoping conversation',
      'Honest recommendation on the best route',
      'Rough timeline & ballpark estimate',
      'Zero obligation',
    ],
  },
];

const PROCESS = [
  { icon: MessagesSquare, title: 'Submit', text: 'Tell us what you need built and how you want to license it.' },
  { icon: ShieldCheck, title: 'Scope & Quote', text: 'We review your requirements and reply within 24 hours with a plan and price.' },
  { icon: Code2, title: 'We Build', text: 'Our team develops, tests and packages your extension for Joomla 4, 5 & 6.' },
  { icon: TrendingUp, title: 'Deliver & Support', text: 'You receive the finished product with docs, plus ongoing support.' },
];

const EXT_TYPES = [
  { value: 'Component', icon: Layers },
  { value: 'Module', icon: Puzzle },
  { value: 'Plugin', icon: Code2 },
  { value: 'Template', icon: Server },
  { value: 'Not sure yet', icon: MessagesSquare },
];

const CustomExtensionsPage = () => {
  const [formData, setFormData] = useState({
    company: '', firstName: '', lastName: '', email: '', phone: '',
    extensionName: '', extensionType: '', pricingPreference: '',
    joomlaVersions: ['4', '5', '6'], budget: '', timeline: '',
    description: '', agreedToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleJoomlaVersion = (v) => {
    setFormData((prev) => ({
      ...prev,
      joomlaVersions: prev.joomlaVersions.includes(v)
        ? prev.joomlaVersions.filter((x) => x !== v)
        : [...prev.joomlaVersions, v].sort(),
    }));
  };

  const selectPricing = (id) => {
    const label = PRICING_OPTIONS.find((o) => o.id === id)?.title || id;
    setFormData((prev) => ({ ...prev, pricingPreference: label }));
    const el = document.getElementById('custom-extension-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.pricingPreference) {
      toast.error('Please choose a pricing option above.');
      return;
    }
    if (formData.joomlaVersions.length === 0) {
      toast.error('Please select at least one Joomla version.');
      return;
    }
    setIsSubmitting(true);

    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    const joomlaStr = formData.joomlaVersions.map((v) => `Joomla ${v}`).join(', ');
    const message = [
      `=== CUSTOM EXTENSION REQUEST ===`,
      `Company / Project: ${formData.company || 'N/A'}`,
      `Contact: ${fullName}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone || 'N/A'}`,
      ``,
      `Extension Name: ${formData.extensionName || 'N/A'}`,
      `Extension Type: ${formData.extensionType}`,
      `Target Joomla Versions: ${joomlaStr}`,
      `Licensing Preference: ${formData.pricingPreference}`,
      `Budget: ${formData.budget || 'Not specified'}`,
      `Timeline: ${formData.timeline || 'Not specified'}`,
      ``,
      `Description / Requirements:`,
      formData.description,
    ].join('\n');

    const tags = [
      'Custom Extension',
      formData.pricingPreference,
      formData.extensionType,
      `Joomla ${formData.joomlaVersions.join('/')}`,
    ].filter(Boolean);

    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fullName,
          email: formData.email,
          phone: formData.phone,
          subject: `Custom Extension Request: ${formData.extensionName || formData.extensionType || 'New Build'}`,
          message,
          source: 'custom_extension',
          business_name: formData.company,
          opportunity_name: `Custom Extension – ${formData.extensionName || formData.company || fullName}`,
          opportunity_source: 'Custom Extension Page',
          stage: 'New Lead',
          tags,
        }),
      });
      if (response.ok) {
        setSubmitted(true);
        toast.success('Request received! We will reply within 24 hours.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 focus:outline-none transition-colors';
  const labelCls = 'block text-sm font-medium text-gray-300 mb-2';

  return (
    <div className="min-h-screen bg-bots-dark" data-testid="custom-extensions-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-20">
        <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] bg-blue-600/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[36rem] h-[36rem] bg-purple-600/20 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-sm font-semibold mb-6">
            <Code2 className="w-4 h-4" /> Custom Joomla Development
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6">
            Custom Extensions,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              built your way
            </span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-8">
            Need a Joomla component, module, plugin or template that doesn't exist yet?
            We'll build it — with flexible pricing whether you want it exclusively yours
            or co-marketed with royalties paid to you for life.
          </p>
          <button
            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-500 text-white font-bold rounded-full hover:from-purple-500 hover:to-indigo-400 transition-all transform hover:scale-105 shadow-lg shadow-purple-500/30"
            data-testid="hero-see-options"
          >
            See pricing options <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Pricing options */}
      <section id="pricing" className="py-16 bg-bots-surface border-y border-blue-500/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Choose how you want it built</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Three flexible ways to bring your Joomla extension to life. Pick the model that fits your goals — you can change your mind when we talk.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PRICING_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const selected = formData.pricingPreference === opt.title;
              return (
                <div
                  key={opt.id}
                  className={`relative rounded-2xl p-8 border transition-all flex flex-col ${
                    selected
                      ? 'border-blue-400 bg-blue-500/5 ring-2 ring-blue-400/40'
                      : opt.highlighted
                      ? 'border-purple-500/40 bg-bots-dark/60 hover:border-purple-400'
                      : 'border-blue-500/15 bg-bots-dark/60 hover:border-blue-400/50'
                  }`}
                  data-testid={`pricing-card-${opt.id}`}
                >
                  {opt.highlighted && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-500 text-white text-xs font-bold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Most popular
                    </span>
                  )}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${opt.accent} flex items-center justify-center mb-5 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-blue-300 bg-blue-500/10 px-2 py-1 rounded-full w-fit mb-3">{opt.badge}</span>
                  <h3 className="text-xl font-bold text-white">{opt.title}</h3>
                  <p className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 my-2">{opt.price}</p>
                  <p className="text-purple-300 text-sm font-medium mb-3">{opt.tagline}</p>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{opt.description}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {opt.features.map((f) => (
                      <li key={f} className="flex items-start text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => selectPricing(opt.id)}
                    className={`w-full py-3 rounded-full font-bold transition-all ${
                      selected
                        ? 'bg-blue-600 text-white'
                        : 'bg-white/5 border border-white/15 text-white hover:bg-white/10'
                    }`}
                    data-testid={`choose-${opt.id}`}
                  >
                    {selected ? 'Selected ✓' : 'Choose this'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-bots-dark">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {PROCESS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="text-center">
                  <div className="relative w-14 h-14 mx-auto mb-4 rounded-xl bg-blue-500/15 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-blue-400" />
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  </div>
                  <h3 className="text-white font-bold mb-1">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="custom-extension-form" className="py-20 bg-gradient-to-b from-bots-dark to-bots-surface">
        <div className="max-w-3xl mx-auto px-4">
          {submitted ? (
            <div className="text-center bg-bots-surface border border-blue-500/20 rounded-2xl p-12" data-testid="cx-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-white mb-3">Request received!</h3>
              <p className="text-gray-300 max-w-md mx-auto mb-6">
                Thanks{formData.firstName ? `, ${formData.firstName}` : ''}! Our team will review your requirements and reply within 24 hours with next steps and a quote.
              </p>
              <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full hover:from-blue-500 hover:to-cyan-400 transition-all">
                Browse our extensions <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Tell us about your extension</h2>
                <p className="text-gray-400">The more detail you share, the more accurate your quote.</p>
              </div>

              <form onSubmit={handleSubmit} className="bg-bots-surface border border-blue-500/20 rounded-2xl p-6 md:p-8 space-y-6" data-testid="custom-extension-form">
                {/* Contact */}
                <div>
                  <label className={labelCls}>Company / Project</label>
                  <input type="text" placeholder="Acme Media" value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })} className={inputCls} data-testid="cx-company" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>First name *</label>
                    <input type="text" required value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className={inputCls} data-testid="cx-firstname" />
                  </div>
                  <div>
                    <label className={labelCls}>Last name *</label>
                    <input type="text" required value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className={inputCls} data-testid="cx-lastname" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input type="email" required value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} data-testid="cx-email" />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input type="tel" value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputCls} data-testid="cx-phone" />
                  </div>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <label className={labelCls}>Extension name / working title</label>
                  <input type="text" placeholder="e.g. Smart Booking Manager" value={formData.extensionName}
                    onChange={(e) => setFormData({ ...formData, extensionName: e.target.value })} className={inputCls} data-testid="cx-name" />
                </div>

                {/* Extension type */}
                <div>
                  <label className={labelCls}>What do you need built? *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {EXT_TYPES.map((t) => {
                      const Icon = t.icon;
                      const active = formData.extensionType === t.value;
                      return (
                        <button key={t.value} type="button"
                          onClick={() => setFormData({ ...formData, extensionType: t.value })}
                          className={`flex flex-col items-center gap-2 p-3 rounded-lg border text-xs font-medium transition-all ${
                            active ? 'border-blue-400 bg-blue-500/10 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                          }`}
                          data-testid={`cx-type-${t.value.replace(/\s+/g, '-').toLowerCase()}`}>
                          <Icon className={`w-5 h-5 ${active ? 'text-blue-400' : ''}`} />
                          {t.value}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Joomla versions */}
                <div>
                  <label className={labelCls}>Target Joomla versions *</label>
                  <div className="flex flex-wrap gap-3" data-testid="cx-joomla-versions">
                    {['4', '5', '6'].map((v) => {
                      const active = formData.joomlaVersions.includes(v);
                      return (
                        <button key={v} type="button" onClick={() => toggleJoomlaVersion(v)}
                          className={`px-5 py-2.5 rounded-lg border font-semibold transition-all ${
                            active ? 'border-blue-400 bg-blue-500/10 text-blue-300' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                          }`}
                          data-testid={`cx-joomla-${v}`}>
                          {active && <CheckCircle className="w-4 h-4 inline mr-1.5 -mt-0.5" />}Joomla {v}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Licensing preference */}
                <div>
                  <label className={labelCls}>Licensing preference *</label>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {PRICING_OPTIONS.map((opt) => {
                      const active = formData.pricingPreference === opt.title;
                      return (
                        <button key={opt.id} type="button"
                          onClick={() => setFormData({ ...formData, pricingPreference: opt.title })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            active ? 'border-blue-400 bg-blue-500/10' : 'border-gray-700 hover:border-gray-500'
                          }`}
                          data-testid={`cx-pref-${opt.id}`}>
                          <span className="block text-sm font-semibold text-white">{opt.title}</span>
                          <span className="block text-xs text-gray-400">{opt.price}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Estimated budget</label>
                    <select value={formData.budget}
                      onChange={(e) => setFormData({ ...formData, budget: e.target.value })} className={inputCls} data-testid="cx-budget">
                      <option value="">Optional</option>
                      <option value="Under $1,000">Under $1,000</option>
                      <option value="$1,000 - $5,000">$1,000 – $5,000</option>
                      <option value="$5,000 - $15,000">$5,000 – $15,000</option>
                      <option value="$15,000+">$15,000+</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Desired timeline</label>
                    <select value={formData.timeline}
                      onChange={(e) => setFormData({ ...formData, timeline: e.target.value })} className={inputCls} data-testid="cx-timeline">
                      <option value="">Optional</option>
                      <option value="ASAP">ASAP</option>
                      <option value="1-2 months">1–2 months</option>
                      <option value="3-6 months">3–6 months</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Describe what your extension should do *</label>
                  <textarea required rows={5} placeholder="Explain the features, integrations, data and workflows you need..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={`${inputCls} resize-none`} data-testid="cx-description" />
                </div>

                <label className="flex items-start text-gray-300 text-sm cursor-pointer">
                  <input type="checkbox" required checked={formData.agreedToTerms}
                    onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                    className="mr-3 mt-1" data-testid="cx-terms" />
                  <span>I agree to be contacted by AI Joomla about my custom extension request.</span>
                </label>

                <button type="submit" disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-500 text-white font-bold rounded-full hover:from-purple-500 hover:to-indigo-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="cx-submit">
                  {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>) : 'Submit Custom Extension Request'}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomExtensionsPage;
